import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { uploadUrl } from '../../api/client';
import { trackEvent } from '../../analytics';
import styles from './Garage.module.css';

export function Garage() {
  const { user, isAuthenticated } = useAuth();
  const { garageCars } = useData();

  const myCars = isAuthenticated && user ? garageCars.filter((c) => c.ownerId === user.id) : [];
  const currentCars = myCars.filter((c) => !c.isPastCar);
  const pastCars = myCars.filter((c) => c.isPastCar);
  const getMaintenanceSummary = (carId: string) => {
    const car = myCars.find((item) => item.id === carId);
    const schedule = car?.maintenanceSchedule ?? [];
    return {
      overdue: schedule.filter((item) => item.status === 'overdue').length,
      dueSoon: schedule.filter((item) => item.status === 'due_soon').length,
    };
  };

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <h1>My garage</h1>
        <div className={styles.gateCard}>
          <p className={styles.signInPrompt}>
            Create an account to build your garage, track your car journey, and show off your current and past rides.
          </p>
          <div className={styles.gateActions}>
            <Link
              to="/create-account"
              className={styles.cta}
              onClick={() => trackEvent('cta_click', { context: 'garage_gate', label: 'create_account' })}
            >
              Create account
            </Link>
            <Link
              to="/login"
              className={styles.secondaryCta}
              onClick={() => trackEvent('cta_click', { context: 'garage_gate', label: 'sign_in' })}
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>My garage</h1>
        <p className={styles.subtitle}>Your current and past cars</p>
        <Link to="/garage/new" className={styles.addBtn}>+ Add car</Link>
      </div>

      {currentCars.length > 0 && (
        <section className={styles.section}>
          <h2>Current</h2>
          <div className={styles.grid}>
            {currentCars.map((car) => {
              const maintenanceSummary = getMaintenanceSummary(car.id);
              return (
                <Link key={car.id} to={`/garage/${car.id}`} className={styles.card}>
                  <div className={styles.cardImage}>
                    {car.photos[0] ? (
                      <img src={car.photos[0].startsWith('http') ? car.photos[0] : uploadUrl(car.photos[0])} alt="" />
                    ) : (
                      <span className={styles.placeholder}>🚗</span>
                    )}
                    {car.forSale && <span className={styles.forSaleBanner}>For Sale</span>}
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.cardTitle}>
                      {car.car.year} {car.car.make} {car.car.model}
                    </span>
                    {car.name && <span className={styles.cardName}>{car.name}</span>}
                    {(maintenanceSummary.overdue > 0 || maintenanceSummary.dueSoon > 0) && (
                      <div className={styles.maintenanceSummary}>
                        {maintenanceSummary.overdue > 0 && (
                          <span className={styles.overdueChip}>{maintenanceSummary.overdue} overdue</span>
                        )}
                        {maintenanceSummary.dueSoon > 0 && (
                          <span className={styles.dueSoonChip}>{maintenanceSummary.dueSoon} due soon</span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {pastCars.length > 0 && (
        <section className={styles.section}>
          <h2>Past cars</h2>
          <div className={styles.grid}>
            {pastCars.map((car) => (
              <Link key={car.id} to={`/garage/${car.id}`} className={styles.card}>
                <div className={styles.cardImage}>
                  {car.photos[0] ? (
                    <img src={car.photos[0].startsWith('http') ? car.photos[0] : uploadUrl(car.photos[0])} alt="" />
                  ) : (
                    <span className={styles.placeholder}>🚗</span>
                  )}
                  {car.forSale && <span className={styles.forSaleBanner}>For Sale</span>}
                </div>
                <div className={styles.cardBody}>
                  <span className={styles.cardTitle}>
                    {car.car.year} {car.car.make} {car.car.model}
                  </span>
                  <span className={styles.pastBadge}>Past</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {myCars.length === 0 && (
        <div className={styles.empty}>
          <p>No cars in your garage yet.</p>
          <Link to="/garage/new" className={styles.cta}>Add your first car</Link>
        </div>
      )}
    </div>
  );
}
